import { html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import 'components/ordering/rma/components/context/rma-context.js'
import 'components/ordering/rma/components/overview/overview.js'
import 'components/ordering/rma/components/overview-header/overview-header.js'
import 'components/ordering/rma/components/rma-request/rma-request.js'
import 'components/ordering/rma/components/detail/detail.js'
import 'components/ordering/rma/components/error/error.js'

type RmaRoute = 'overview' | 'request' | 'detail' | 'error' | 'server-rendered'

const DRAFT_PATH = /\/rma\/draft\/?$/
const ERROR_PATH = /\/rma\/error\/([^/]+)\/?$/
const DETAIL_PATH = /\/rma\/detail\/([^/]+)\/?$/
const LIST_PATH = /\/rma\/?$/

@customElement('csc-rma')
export class CSCRma extends LitElement {
  protected override createRenderRoot = () => this

  @property({ attribute: 'base-path', type: String })
  public basePath = ''

  @property({ attribute: 'locale', type: String })
  public locale = ''

  @property({ attribute: 'upload-url', type: String })
  public uploadUrl = ''

  @property({ attribute: 'enable-rma-comment', type: Boolean })
  public enableRmaComment = false

  @property({ attribute: 'discard-url', type: String })
  public discardUrl = ''

  @property({ attribute: 'list-url', type: String })
  public listUrl = ''

  // The paginator links navigate with ?page=N; the overview needs that page when
  // it fetches the list itself.
  @property({ attribute: 'page', type: Number })
  public currentPage = 1

  @property({ attribute: 'draft-rma-url', type: String })
  public draftRmaUrl = ''

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  private get pathname(): string {
    return this.windowLocation?.pathname ?? ''
  }

  private get route(): RmaRoute {
    if (DRAFT_PATH.test(this.pathname)) return 'request'
    if (ERROR_PATH.test(this.pathname)) return 'error'
    // A template that still injects the data server-side owns the page; rendering
    // our own copy next to it would show the list or detail twice.
    if (this.hasServerRenderedContent) return 'server-rendered'
    if (DETAIL_PATH.test(this.pathname)) return 'detail'
    if (LIST_PATH.test(this.pathname)) return 'overview'
    return 'server-rendered'
  }

  private get hasServerRenderedContent(): boolean {
    return document.querySelector('csc-rma-overview[rmas], csc-rma-detail[rma]') !== null
  }

  private renderRoute(): TemplateResult | typeof nothing {
    switch (this.route) {
      case 'overview':
        return html`
          <csc-rma-overview-header draft-rma-url=${this.draftRmaUrl}></csc-rma-overview-header>
          <csc-rma-overview
            locale=${this.locale}
            page=${this.currentPage}
            draft-rma-url=${this.draftRmaUrl}
          ></csc-rma-overview>
        `
      case 'detail':
        return html`
          <csc-rma-detail
            locale=${this.locale}
            rma-id=${DETAIL_PATH.exec(this.pathname)?.[1] ?? ''}
          ></csc-rma-detail>
        `
      case 'request':
        return html`
          <csc-rma-request discard-url=${this.discardUrl} list-url=${this.listUrl}></csc-rma-request>
        `
      case 'error':
        return html`
          <csc-rma-error
            status=${ERROR_PATH.exec(this.pathname)?.[1] ?? ''}
            back-url=${this.listUrl}
          ></csc-rma-error>
        `
      default:
        return nothing
    }
  }

  override render() {
    if (!this.basePath) return nothing

    return html`
      <csc-rma-context
        base-path=${this.basePath}
        locale=${this.locale}
        upload-url=${this.uploadUrl}
        ?enable-rma-comment=${this.enableRmaComment}
      >
        ${this.renderRoute()}
      </csc-rma-context>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma': CSCRma
  }
}
